/**
 * CREATIVE DETONATOR - Dummy Data Generator
 * Provides static data for simulation and testing
 *
 * This module is separate from dev-mode.js to avoid circular dependencies.
 * Phase 3 detonation can import this without importing UI code.
 *
 * IMPORTANT: Dummy images are ONLY added here for dev/testing mode.
 * Real generations will NOT have dummy images - they use the placeholder UI.
 */

// v2 - Added dummy images for dev/testing mode
import { GeneratorState } from './state.js';
import { setFormats, setTriggers } from '../../../js/store/actions/generator.js';

// Access DataStore from global scope (defined in main.js)
const DataStore = window.DataStore;

// Dummy images for dev/testing mode ONLY
// These are only used when populateDummyData() is called (dev jump to Phase 4)
const DUMMY_IMAGE_PATHS = [
  '/assets/images/dummy/print1.png',
  '/assets/images/dummy/print2.png',
  '/assets/images/dummy/print3.png',
  '/assets/images/dummy/print4.png'
];

// ============================================================================
// DUMMY BRIEF
// ============================================================================

/**
 * Get a default dummy brief for testing
 * @returns {Object} Brief object with product, benefit, formats
 */
export function getDummyBrief() {
  return {
    product: 'Electric Golf Cart',
    benefit: 'Silent driving in the city',
    formats: ['video', 'social', 'print', 'experiential', 'digital', 'campaign']
  };
}

// ============================================================================
// DUMMY IDEAS GENERATION
// ============================================================================

/**
 * Generate dummy ideas for testing Phase 4 UI
 * Creates a comprehensive set of ideas across all 6 formats
 * Populates GeneratorState.generatedIdeas directly
 */
export function populateDummyData() {
  GeneratorState.generatedIdeas = [];

  // Use all formats for comprehensive testing
  setFormats(['social', 'video', 'print', 'experiential', 'digital', 'campaign']);

  // ========================================================================
  // PRINT format - 3 ideas (A1 trigger - Magenta)
  // WITH dummy images for dev/testing
  // ========================================================================
  const triggerA1 = DataStore.getTriggerByCode('A1');
  if (triggerA1) {
    GeneratorState.generatedIdeas.push({
      trigger: triggerA1,
      ideas: [
        {
          id: 'A1-print-1',
          title: 'The Decibel Deception',
          format: 'print',
          idea: 'Silence so complete it can be measured—and the measurement itself becomes the message.',
          text: 'Full-page print ad: Black background. Giant "0 dB" in white. Below in tiny text: "The sound of our engine." At bottom: the car, small, elegant.',
          visual: 'Full-page print ad: Black background. Giant "0 dB" in white. Below in tiny text: "The sound of our engine." At bottom: the car, small, elegant.',
          headline: '0 dB',
          subline: 'The sound of our engine.',
          alternativeHeadlines: [
            { headline: 'Nothing to hear here.', subline: 'Literally.' },
            { headline: 'The loudest silence on the road.', subline: null }
          ],
          currentHeadlineIndex: 0,
          sourceFragment: 'The moment you realize your car made no sound entering the driveway',
          sourceTruth: 'People judge power by noise—silence feels like a glitch in reality',
          sectionLabel: 'VISUAL',
          headlineLabel: 'HEADLINE',
          whyItWorks: 'Cognitive game - makes reader work to understand, creating deeper engagement.',
          // DEV MODE: Dummy image for testing
          imageUrl: DUMMY_IMAGE_PATHS[0]
        },
        {
          id: 'A1-print-2',
          title: 'The Empty Waveform',
          format: 'print',
          idea: 'The visual language of sound reveals the absence of sound.',
          text: 'Billboard showing a flat-line audio waveform (like silence on a recording). Below it: the car. No headline needed - the visual IS the message.',
          visual: 'Billboard showing a flat-line audio waveform (like silence on a recording). Below it: the car. No headline needed - the visual IS the message.',
          headline: 'The sound of nothing.',
          subline: 'Now available in electric.',
          alternativeHeadlines: [
            { headline: 'Flatline. The good kind.', subline: 'Zero decibels. Zero emissions.' },
            { headline: 'This is what quiet looks like.', subline: null }
          ],
          currentHeadlineIndex: 0,
          sourceFragment: 'Audio engineers can\'t record what isn\'t there',
          sourceTruth: 'We trust visual proof of invisible things—waveforms make sound tangible',
          sectionLabel: 'VISUAL',
          headlineLabel: 'HEADLINE',
          whyItWorks: 'Pattern interrupt - billboards are loud, this one is visually silent.',
          // DEV MODE: Dummy image for testing
          imageUrl: DUMMY_IMAGE_PATHS[1]
        },
        {
          id: 'A1-print-3',
          title: 'The Sound Test',
          format: 'print',
          idea: 'Same world, different experience—the decibel meter tells the truth.',
          text: 'Split image: Left side shows decibel meter at 85dB with gas car. Right side shows meter at 0dB with our car. Same street, same angle, different reality.',
          visual: 'Split image: Left side shows decibel meter at 85dB with gas car. Right side shows meter at 0dB with our car. Same street, same angle, different reality.',
          headline: 'Same street. Different soundtrack.',
          subline: 'Guess which one is ours.',
          alternativeHeadlines: [
            { headline: '85 vs 0. You do the math.', subline: 'The quieter choice.' },
            { headline: 'Spot the difference. (Hint: use your ears.)', subline: null }
          ],
          currentHeadlineIndex: 0,
          sourceFragment: 'Standing at a crosswalk, trying to figure out which car is moving',
          sourceTruth: 'Our brains use sound as a safety signal—silence from machines feels wrong',
          sectionLabel: 'VISUAL',
          headlineLabel: 'HEADLINE',
          whyItWorks: 'Direct comparison creates impossible-to-ignore proof point.',
          // DEV MODE: Dummy image for testing
          imageUrl: DUMMY_IMAGE_PATHS[2],
          // REFINED IDEA FIELDS - Shows Art Director refinement UI
          isRefined: true,
          refinementCount: 1,
          critique: 'The original headline was too clever and buried the benefit. "Same street. Different soundtrack." immediately sets up the A/B comparison while the subline creates intrigue. The visual description now emphasizes the decibel meters as the hero element.',
          _original: {
            headline: 'Two cars. One sound.',
            subline: 'Actually, zero sounds.',
            visual: 'Side-by-side comparison of two cars on the same street.'
          }
        }
      ]
    });
  }

  // ========================================================================
  // VIDEO format - 3 ideas (B2 trigger - Orange)
  // ========================================================================
  const triggerB2 = DataStore.getTriggerByCode('B2');
  if (triggerB2) {
    GeneratorState.generatedIdeas.push({
      trigger: triggerB2,
      ideas: [
        {
          id: 'B2-video-1',
          title: 'The Lullaby Test',
          format: 'video',
          idea: 'A father\'s 2AM drive home becomes proof that silence is an act of love.',
          text: 'DURATION: :30\n\nSHOTS:\nOPEN on father driving at 2AM (0s-5s)\nPOV passing sleeping suburb (5s-15s)\nDogs don\'t bark, babies don\'t wake (15s-22s)\nFather carries sleeping child inside (22s-28s)\nEND FRAME: Logo + tagline (28s-30s)\n\nAUDIO:\nAmbient night sounds only. No engine. No music until end frame.',
          visual: 'DURATION: :30\n\nSHOTS:\nOPEN on father driving at 2AM (0s-5s)\nPOV passing sleeping suburb (5s-15s)\nDogs don\'t bark, babies don\'t wake (15s-22s)\nFather carries sleeping child inside (22s-28s)\nEND FRAME: Logo + tagline (28s-30s)\n\nAUDIO:\nAmbient night sounds only. No engine. No music until end frame.',
          headline: 'The engine that never woke a soul.',
          alternativeHeadlines: [
            { headline: 'Shh. Dad\'s home.', subline: null },
            { headline: 'Some things should stay asleep.', subline: null }
          ],
          currentHeadlineIndex: 0,
          sourceFragment: 'The guilt of waking your family when you come home late',
          sourceTruth: 'Parents carry invisible guilt about disrupting their family\'s peace',
          sectionLabel: 'EXECUTION',
          headlineLabel: 'SUPER',
          whyItWorks: 'Emotional trigger connects silence to parenting love.'
        },
        {
          id: 'B2-video-2',
          title: 'The Sound Engineer',
          format: 'video',
          idea: 'A professional whose job is capturing sound meets something uncapturable.',
          text: 'DURATION: :15\n\nSHOTS:\nSound engineer with pro mic approaches car (0s-3s)\nHe tries recording from every angle (3s-10s)\nLooks at empty waveform, confused (10s-13s)\nShrugs at camera, smiles (13s-15s)\n\nAUDIO:\nSilence throughout. Text: "We tried to record our engine."',
          headline: 'Nothing to hear here.',
          sectionLabel: 'EXECUTION',
          headlineLabel: 'SUPER',
          whyItWorks: 'Humor through absurd demonstration makes the point memorable.'
        },
        {
          id: 'B2-video-3',
          title: 'The Wildlife Return',
          format: 'video',
          idea: 'When cars go silent, nature reclaims the soundscape.',
          text: 'DURATION: :60\n\nSHOTS:\nTime-lapse of noisy street, birds flee (0s-15s)\nOur cars replace gas cars over weeks (15s-30s)\nBirds return, nature sounds emerge (30s-50s)\nPull out to reveal transformed neighborhood (50s-60s)\n\nAUDIO:\nChaos → silence → birdsong',
          headline: 'When we went quiet, nature came back.',
          sectionLabel: 'EXECUTION',
          headlineLabel: 'SUPER',
          whyItWorks: 'Environmental emotion paired with visual transformation.'
        }
      ]
    });
  }

  // ========================================================================
  // SOCIAL format - 3 ideas (C1 trigger - Cyan)
  // WITH dummy images for dev/testing
  // ========================================================================
  const triggerC1 = DataStore.getTriggerByCode('C1');
  if (triggerC1) {
    GeneratorState.generatedIdeas.push({
      trigger: triggerC1,
      ideas: [
        {
          id: 'C1-social-1',
          title: 'The Silent Challenge',
          format: 'social',
          idea: 'Turn viewers into active listeners—then reward them with nothing.',
          text: 'FORMAT: Reel / 9:16 / 15s\n\nHOOK (First Frame):\nText: "Try to hear our engine" over video of car driving\n\nSEQUENCE:\n1. Car drives past camera (0s-5s)\n2. "Did you hear it?" text (5s-8s)\n3. Replay with volume meter showing 0 (8s-13s)\n4. Logo reveal (13s-15s)\n\nSOUND: Trending audio that goes silent mid-way',
          headline: 'POV: You\'re trying to hear our engine\n\n#SilentRevolution #ElectricLife',
          sectionLabel: 'EXECUTION',
          headlineLabel: 'CAPTION',
          whyItWorks: 'Interactive hook makes viewers actively listen (and hear nothing).',
          // DEV MODE: Dummy image for testing
          imageUrl: DUMMY_IMAGE_PATHS[3]
        },
        {
          id: 'C1-social-2',
          title: 'The ASMR Drive',
          format: 'social',
          idea: 'Hijack the ASMR trend to prove our engine isn\'t part of the soundtrack.',
          text: 'FORMAT: Reel / 9:16 / 30s\n\nHOOK (First Frame):\nClose-up of tire on gravel with "Sound ON" text\n\nSEQUENCE:\n1. ASMR-style: tire sounds, wind, birds (0s-20s)\n2. Reveal: we\'re in a moving car (20s-25s)\n3. "The only sounds you\'ll hear" (25s-30s)\n\nSOUND: High-quality ambient audio, NO engine',
          headline: 'Your commute as ASMR\n\nThe sounds of driving. Minus one.\n\n#ASMR #QuietLuxury',
          sectionLabel: 'EXECUTION',
          headlineLabel: 'CAPTION',
          whyItWorks: 'Hijacks ASMR trend to demonstrate product truth.',
          // DEV MODE: Dummy image for testing
          imageUrl: DUMMY_IMAGE_PATHS[0]
        },
        {
          id: 'C1-social-3',
          title: 'Comment Bait',
          format: 'social',
          idea: 'A question with no answer becomes the answer itself.',
          text: 'FORMAT: Static / 1:1\n\nHOOK (First Frame):\nImage of our car with text: "Name one thing you can hear in this photo"\n\nSEQUENCE: N/A - Static image\n\nSOUND: N/A',
          headline: 'Wrong answers only\n\n(Hint: there is no right answer)\n\n#GuessTheSound #ElectricCar',
          sectionLabel: 'EXECUTION',
          headlineLabel: 'CAPTION',
          whyItWorks: 'Engagement bait that makes the silence point through interaction.',
          // DEV MODE: Dummy image for testing
          imageUrl: DUMMY_IMAGE_PATHS[1]
        }
      ]
    });
  }

  // ========================================================================
  // EXPERIENTIAL format - 3 ideas (E3 trigger - Purple)
  // ========================================================================
  const triggerE3 = DataStore.getTriggerByCode('E3');
  if (triggerE3) {
    GeneratorState.generatedIdeas.push({
      trigger: triggerE3,
      ideas: [
        {
          id: 'E3-exp-1',
          title: 'The Silence Room',
          format: 'experiential',
          idea: 'Put chaos outside a glass wall—and let people feel the peace within.',
          text: 'SCALE: Mid-scale\n\nSETUP:\nSoundproof glass cube in Times Square. Our car inside. Visitors enter through airlock.\n\nINTERACTION:\nSit in car for 60 seconds. Watch the chaos outside in complete silence.\n\nTHE REVEAL:\nWhen time ends, sign lights up: "This is your daily commute now."\n\nCAPTURE:\nPhoto booth prints pic with decibel reading "0dB"\n\nTAKEAWAY: Custom earplugs branded "For when you\'re not in ours"',
          headline: '',
          sectionLabel: 'ACTIVATION',
          headlineLabel: '',
          whyItWorks: 'Experiential proof - you can\'t argue with what you felt.'
        },
        {
          id: 'E3-exp-2',
          title: 'The Library Test Drive',
          format: 'experiential',
          idea: 'The only place where shushing applies to people, not cars.',
          text: 'SCALE: Guerrilla\n\nSETUP:\nPark car inside actual library. "Test drives available" sign at entrance.\n\nINTERACTION:\nDrive through library aisles at 5mph. Librarian shushes other patrons, ignores car.\n\nTHE REVEAL:\n"The only car quiet enough for the library" banner at exit.\n\nCAPTURE:\nFilm reactions, library partnership PR\n\nTAKEAWAY: Library card with test drive redemption code',
          headline: '',
          sectionLabel: 'ACTIVATION',
          headlineLabel: '',
          whyItWorks: 'Absurd location creates viral moment and undeniable proof.'
        },
        {
          id: 'E3-exp-3',
          title: 'Sound Swap',
          format: 'experiential',
          idea: 'Hear what you\'re NOT hearing by playing someone else\'s noise.',
          text: 'SCALE: Premium\n\nSETUP:\nTwo identical driving courses. One with gas car, one with ours. Noise-measuring stations throughout.\n\nINTERACTION:\nDrive both courses. Wear headphones that play the OTHER car\'s sound.\n\nTHE REVEAL:\nYou heard the gas car while driving ours - proving how silent yours was.\n\nCAPTURE:\nPersonalized sound comparison video emailed after\n\nTAKEAWAY: Certificate showing decibels saved',
          headline: '',
          sectionLabel: 'ACTIVATION',
          headlineLabel: '',
          whyItWorks: 'Direct A/B comparison with sensory twist makes it unforgettable.'
        }
      ]
    });
  }

  // ========================================================================
  // DIGITAL format - 3 ideas (H1 trigger - Red)
  // ========================================================================
  const triggerH1 = DataStore.getTriggerByCode('H1');
  if (triggerH1) {
    GeneratorState.generatedIdeas.push({
      trigger: triggerH1,
      ideas: [
        {
          id: 'H1-digital-1',
          title: 'The Budget Breakdown',
          format: 'digital',
          idea: 'Make our $0 sound budget the punchline—and the proof.',
          text: 'SIZES: 300x250, 728x90, 160x600\n\nANIMATION:\nF1 (0s-2s): "Typical car ad sound budget"\nF2 (2s-4s): Line items appear ($50K engine, $30K vroom...)\nF3 (4s-6s): "Our budget:" followed by "$0"\nF4 (6s-8s): Static end frame with CTA\n\nINTERACTION: Hover reveals "We literally had nothing to work with"',
          headline: 'Hear for yourself',
          sectionLabel: 'ANIMATION',
          headlineLabel: 'CTA',
          whyItWorks: 'Transparency about limitation becomes flex.'
        },
        {
          id: 'H1-digital-2',
          title: 'The Volume Slider',
          format: 'digital',
          idea: 'A control that does nothing becomes a demonstration of nothing to control.',
          text: 'SIZES: 300x600, 300x250\n\nANIMATION:\nF1 (0s-3s): Interactive volume slider appears\nF2 (3s-5s): User drags it - nothing changes\nF3 (5s-7s): Text: "Our engine has no volume"\nF4 (7s+): Static end frame\n\nINTERACTION: Draggable slider that does nothing (the point)',
          headline: 'Test drive the silence',
          sectionLabel: 'ANIMATION',
          headlineLabel: 'CTA',
          whyItWorks: 'Interactive disappointment becomes memorable demonstration.'
        },
        {
          id: 'H1-digital-3',
          title: 'Loading Silence',
          format: 'digital',
          idea: 'The file doesn\'t exist because the sound doesn\'t exist.',
          text: 'SIZES: 970x250, 728x90\n\nANIMATION:\nF1 (0s-2s): "Loading engine sound..."\nF2 (2s-4s): Progress bar fills\nF3 (4s-6s): "Error: File not found"\nF4 (6s+): Car image + "Some things don\'t exist"\n\nINTERACTION: Static',
          headline: 'Discover what\'s missing',
          sectionLabel: 'ANIMATION',
          headlineLabel: 'CTA',
          whyItWorks: 'Subverts loading expectation for comedic payoff.'
        }
      ]
    });
  }

  // ========================================================================
  // CAMPAIGN format - 3 ideas (M2 trigger - Emerald)
  // ========================================================================
  const triggerM2 = DataStore.getTriggerByCode('M2');
  if (triggerM2) {
    GeneratorState.generatedIdeas.push({
      trigger: triggerM2,
      ideas: [
        {
          id: 'M2-campaign-1',
          title: 'The Silence Movement',
          format: 'campaign',
          idea: 'Reframe silence as presence—the peace you add, not the noise you remove.',
          text: 'THE BIG IDEA:\nSilence isn\'t absence—it\'s presence. We\'re not removing noise, we\'re adding peace.\n\nHERO EXECUTION:\n:60 manifesto film. Montage of city noise → our cars appearing → silence spreading → nature returning → "Join the quiet revolution"\n\nEXTENSIONS:\nSOCIAL: #QuietRevolution challenge\nOOH: Billboards in loudest intersections\nPARTNERSHIP: Noise pollution NGOs\nPRODUCT: Branded earplugs for test drives\nPR: "Quietest traffic jam" Guinness record attempt\n\nPARTICIPATION: Noise diary app - track your daily decibels, see what ours would be',
          headline: 'Join the quiet revolution.',
          sectionLabel: 'PLATFORM',
          headlineLabel: 'CAMPAIGN LINE',
          whyItWorks: 'Transforms product feature into cultural movement.'
        },
        {
          id: 'M2-campaign-2',
          title: 'The Last Loud Day',
          format: 'campaign',
          idea: 'Position today\'s engine noise as tomorrow\'s museum exhibit.',
          text: 'THE BIG IDEA:\nOne day, combustion engines will be in museums. We\'re accelerating that future.\n\nHERO EXECUTION:\nDocumentary-style :90 showing "The Last Loud Day" - when gas cars were finally outnumbered.\n\nEXTENSIONS:\nSOCIAL: "What will you NOT miss?" UGC campaign\nOOH: Countdown billboards to "Last Loud Day"\nEXPERIENTIAL: Pop-up "Noise Museum" with recordings of engines\nPRINT: Vintage-style ads for "the sound of the past"\n\nPARTICIPATION: Pledge wall - commit to going silent by [date]',
          headline: 'The future is already here. It\'s just quiet.',
          sectionLabel: 'PLATFORM',
          headlineLabel: 'CAMPAIGN LINE',
          whyItWorks: 'Creates inevitability narrative that makes purchase feel like joining the future.'
        },
        {
          id: 'M2-campaign-3',
          title: 'Sounds of the City',
          format: 'campaign',
          idea: 'Cities have soundtracks. What if we changed the playlist?',
          text: 'THE BIG IDEA:\nCities have soundtracks. Let\'s change the playlist.\n\nHERO EXECUTION:\nPartnership with Spotify. Album of city sounds WITHOUT engines. "The sounds you\'ve been missing."\n\nEXTENSIONS:\nSOCIAL: "City ASMR" content series\nOOH: QR codes in loud areas → play what it COULD sound like\nDIGITAL: Interactive map of city noise → our noise\nACTIVATION: Pop-up recording studio capturing "new city sounds"\n\nPARTICIPATION: Submit sounds from your neighborhood, get featured on album',
          headline: 'Every city deserves a better soundtrack.',
          sectionLabel: 'PLATFORM',
          headlineLabel: 'CAMPAIGN LINE',
          whyItWorks: 'Music/culture partnership makes silence aspirational not clinical.'
        }
      ]
    });
  }

  // Set the triggers to match dummy data
  setTriggers(['A1', 'B2', 'C1', 'E3', 'H1', 'M2']);

  // Sync dummy images to preloadedImages for cross-module access
  // This ensures getIdeaImageUrl() can find them
  if (!GeneratorState.preloadedImages) {
    GeneratorState.preloadedImages = {};
  }

  GeneratorState.generatedIdeas.forEach(group => {
    group.ideas.forEach(idea => {
      if (idea.imageUrl) {
        GeneratorState.preloadedImages[idea.id] = idea.imageUrl;
      }
    });
  });

  console.log('[DummyData] Populated', GeneratorState.generatedIdeas.length, 'trigger groups with',
    GeneratorState.generatedIdeas.reduce((sum, g) => sum + g.ideas.length, 0), 'total ideas');
  console.log('[DummyData] Preloaded', Object.keys(GeneratorState.preloadedImages).length, 'dummy images for testing');
}

// ============================================================================
// GLOBAL EXPORTS FOR COMPATIBILITY
// ============================================================================

window.populateDummyData = populateDummyData;
window.getDummyBrief = getDummyBrief;
